import { Injectable } from '@angular/core';

import { generateIdempotencyKey, IdempotentWritePath } from '../helpers/idempotency.helper';

/**
 * Mints and remembers the `X-Idempotency-Key` sent on fund/grant/FX writes, one scope per endpoint.
 *
 * The contract the backend relies on: one key per *user-initiated submission*, reused across every
 * attempt at that same submission, and replaced for a genuinely different one. IdempotencyInterceptor
 * is the only caller that needs a key; screens only ever call {@link release}.
 */
@Injectable()
export class IdempotencyKeyService {
  private readonly entries = new Map<IdempotentWritePath, { key: string; fingerprint: string }>();

  /**
   * Key for the submission described by `payload`.
   *
   * Keyed on the payload rather than on the click, so a double-fired button, an rxjs `retry()`, or a
   * re-click after a failure all send the *same* key and let the backend dedupe — while an edited
   * payload (user changed the amount) mints a new one. Sending an old key with a changed payload is
   * precisely what the gateway answers with 409, so do not widen this to ignore `payload`.
   */
  keyFor(scope: IdempotentWritePath, payload: unknown): string {
    const fingerprint = this.fingerprint(payload);
    const entry = this.entries.get(scope);

    if (entry?.fingerprint === fingerprint) {
      return entry.key;
    }

    const key = generateIdempotencyKey();
    this.entries.set(scope, { key, fingerprint });
    return key;
  }

  /**
   * Forget the key for `scope`, so the next submission is treated as a new one.
   *
   * The interceptor does this once a submission settles — on success, and on a 409 where the key is
   * spent. Screens call it when a form is (re)initialised, which covers the user abandoning a
   * submission and later re-entering identical values.
   */
  release(scope: IdempotentWritePath): void {
    this.entries.delete(scope);
  }

  private fingerprint(payload: unknown): string {
    try {
      return JSON.stringify(payload) ?? '';
    } catch {
      // Cyclic payload: fall back to "every attempt is a new submission" rather than breaking the
      // submit. Worse deduplication, still a valid key.
      return generateIdempotencyKey();
    }
  }
}
